import React from 'react';
import {
  Badge, Box, Button, Flex, HStack, IconButton, Input, Select, Text,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import useUsers from '../hooks/useUsers';
import { UserDto } from '../models/user';
import { ProjectLeadAssignment } from '../models/project';
import { validateLeads } from '../utils/leadValidation';

interface Props {
  leads: ProjectLeadAssignment[];
  onChange: (leads: ProjectLeadAssignment[]) => void;
}

// item 526 — the reusable "Who is the project lead?" picker (N leads + % split summing to
// 100%). Extracted so the WO-stage "Assign project lead" modal shows the SAME control the
// proposal approval wizard uses. Project-lead commission pays ONLY the assigned lead(s), split
// by %; one lead = 100%, co-leads split to 100%.
const ProjectLeadFields: React.FC<Props> = ({ leads, onChange }) => {
  const { data: users } = useUsers(true);
  const {
    total, balanced, unique, allPositive,
  } = validateLeads(leads);
  const ok = balanced && unique && allPositive; // item 555 — full validity (incl. no dupes / no 0%)

  const setUser = (idx: number, userId: string) => onChange(
    leads.map((l, i) => (i === idx ? { ...l, userId } : l)),
  );
  const setPercent = (idx: number, percent: number) => onChange(
    leads.map((l, i) => (i === idx ? { ...l, percent } : l)),
  );
  const remove = (idx: number) => onChange(leads.filter((_, i) => i !== idx));
  const add = () => onChange([...leads, { userId: '', percent: leads.length === 0 ? 100 : 0 }]);

  return (
    <Box borderWidth="1px" borderColor={leads.length > 0 && !ok ? 'red.400' : 'whiteAlpha.300'} borderRadius="md" p={4}>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="semibold">Who is the project lead?</Text>
        {leads.length > 0 && (
          <Badge colorScheme={ok ? 'green' : 'red'} fontSize="0.75em">
            {`Total ${total % 1 === 0 ? total : total.toFixed(2)}%`}
            {!unique && ' — duplicate lead'}
            {unique && !allPositive && ' — 0% not allowed'}
            {unique && allPositive && !balanced && ' — must equal 100%'}
            {ok && ' ✓'}
          </Badge>
        )}
      </HStack>
      <Text fontSize="xs" color="gray.500" mb={3}>
        The assigned lead earns the project-lead commission on this job. Add a second lead to
        split it — the percentages divide that commission and must total 100%.
      </Text>
      {leads.map((lead, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <HStack key={idx} mb={2}>
          <Select
            flex="1"
            placeholder="— Select lead —"
            value={lead.userId}
            onChange={(e) => setUser(idx, e.target.value)}
          >
            {users?.index.map((u: UserDto) => (
              // item 555 — a user already chosen in another row is disabled (no duplicate leads)
              <option key={u.id} value={u.id} disabled={leads.some((l, i) => i !== idx && l.userId === u.id)}>{u.name}</option>
            ))}
          </Select>
          <Flex align="center" w="110px">
            <Input
              type="number"
              min={0}
              max={100}
              step="any"
              textAlign="right"
              onFocus={(e) => e.currentTarget.select()} // item 555 — select the sticky 0 on focus
              value={Number.isFinite(lead.percent) ? lead.percent : 0}
              onChange={(e) => setPercent(idx, parseFloat(e.target.value) || 0)}
            />
            <Text ml={1} color="gray.500">%</Text>
          </Flex>
          <IconButton
            aria-label="Remove lead"
            icon={<CloseIcon boxSize={2.5} />}
            size="sm"
            variant="ghost"
            onClick={() => remove(idx)}
          />
        </HStack>
      ))}
      <Button size="sm" variant="outline" leftIcon={<AddIcon boxSize={2.5} />} onClick={add}>
        {leads.length === 0 ? 'Assign project lead' : 'Add co-lead (split)'}
      </Button>
    </Box>
  );
};

export default ProjectLeadFields;
